import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Buttons, Button } from '@safelyq/bulma-ui-library';
import { TbAlertTriangle } from "react-icons/tb";
import { IconWrapper } from 'features/admin-campaign-feature/src/components/AddCampaign/StyledComponent';
import useScreenWidth from 'features/base-feature/src/components/CustomHookComponent/RenderingOnScreenSize';
import { IoClose } from "react-icons/io5";

// Context that tracks if there are unsaved changes
const UnsavedChangesContext = createContext(null);

// Register before BrowserRouter is constructed. The provider assigns the
// active handler only while a form is dirty.
let activePopStateGuard = null;
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (event) => {
    activePopStateGuard?.(event);
  }, true);
}

export const UnsavedChangesProvider = ({ children }) => {
  const [dirty, setDirty] = useState(false);
  const [modalCampaignTitle, setModalCampaignTitle] = useState("");

  const markClean = useCallback(() => setDirty(false), []);

  const [showModal, setShowModal] = useState(false);
  const pendingNavigation = React.useRef(null);
  const allowNavigation = React.useRef(false);
  const restoringBrowserHistory = React.useRef(false);

  // ocClose is the pre function of the router/link
  const guardNavigation = useCallback((navigate, onClose) => {

    if (dirty) {
      setShowModal(true);
      // Store a wrapper that performs navigation and then optional cleanup
      pendingNavigation.current = () => {
        navigate?.();
        if (onClose) onClose();
      };
    } else {
      // If no unsaved changes, execute immediately
      if (onClose) onClose();
      navigate?.();
    }
  }, [dirty]);

  const handleLeave = () => {
    setShowModal(false);
    setDirty(false);
    if (pendingNavigation.current) {
      const navigate = pendingNavigation.current;
      pendingNavigation.current = null;

      // The user already confirmed through our custom modal, so let this
      // navigation pass through the popstate guard without asking again.
      allowNavigation.current = true;
      const resetImmediately = navigate();
      if (resetImmediately !== false) {
        allowNavigation.current = false;
      }
    }
  };

  const handleStay = () => {
    setShowModal(false); // close the modal
    pendingNavigation.current = null;
  };

  // On close tab or browser
  useEffect(() => {
    if (!dirty) return;

    const handler = (e) => {
      e.preventDefault();
      e.returnValue = ""; // required for Chrome
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Capture browser Back before React Router receives the POP event. This
  // version of OidcSecure remounts its protected child on every router POP,
  // including a cancelled one, which would reset the campaign form.
  useEffect(() => {
    if (!dirty) return;

    const handlePopState = (event) => {
      if (allowNavigation.current) {
        allowNavigation.current = false;
        return;
      }

      // The Forward POP restores the campaign entry. Hide that event from
      // React Router too, then show the confirmation modal.
      if (restoringBrowserHistory.current) {
        event.stopImmediatePropagation();
        restoringBrowserHistory.current = false;
        setShowModal(true);
        return;
      }

      event.stopImmediatePropagation();
      restoringBrowserHistory.current = true;
      pendingNavigation.current = () => {
        allowNavigation.current = true;
        window.history.back();
        window.setTimeout(() => {
          allowNavigation.current = false;
        }, 500);
        return false;
      };

      // Restore the campaign URL without letting React Router see either POP.
      window.history.forward();
    };

    activePopStateGuard = handlePopState;

    return () => {
      if (activePopStateGuard === handlePopState) {
        activePopStateGuard = null;
      }
      restoringBrowserHistory.current = false;
    };
  }, [dirty]);

  const value = useMemo(() => ({
    dirty,
    setDirty,
    markClean,
    guardNavigation,
    setModalCampaignTitle
  }), [dirty, markClean, guardNavigation]);

  const screenWidth = useScreenWidth();
  const isMobileOrTablet = screenWidth <= 768;
  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      {showModal && (

        <Modal active={true} clipped>
          <Modal.Background />
          <Modal.Card className="is-flex is-justify-content-center is-align-items-center">
            <Modal.Card.Body style={{ width: isMobileOrTablet ? "95%" : "72%", borderRadius: "12px", padding: isMobileOrTablet ? "1.5rem" : "2rem" }}>
              <div className="is-flex is-justify-content-end is-align-items-center">
                <IoClose className='has-text-grey' onClick={handleStay} style={{ cursor: "pointer", width: "24px", height: "24px" }} />
              </div>
              <div className='is-flex is-flex-direction-column is-justify-content-center is-align-items-center'>
                <IconWrapper>
                  <TbAlertTriangle style={{ color: "#bb8c00ff", width: "32", height: "32" }} />
                </IconWrapper>
                <p className='has-text-weight-bold has-text-centered is-size-5 mt-2'>You have unsaved changed</p>
                <p className='has-text-centered is-size-6 mt-2'>
                  Are you sure you want to leave? Your campaign
                  <span className='has-text-weight-bold has-text-info'> {modalCampaignTitle || "Untitled Campaign"} </span>
                  has not been saved and your progress will be lost</p>
              </div>

              <Buttons className='is-flex is-flex-direction-column is-align-items-center mt-5'>
                <Button style={{ width: "100%" }} className='has-background-success-dark has-text-white' onClick={handleStay}>Stay and Keep Editing</Button>
                <Button style={{ width: "100%", marginRight: "8px" }} className='has-background-danger-light has-text-danger' onClick={handleLeave}>Leave and Discard Changes</Button>
              </Buttons>

            </Modal.Card.Body>
          </Modal.Card>
        </Modal>
      )}
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);
